import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LogOut, RotateCcw, Compass, BookHeart, Mountain, Tent, Sparkles, PawPrint, Footprints, Loader2, Save, Target, Camera, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useUnits } from "../lib/units";
import { api } from "../lib/api";
import EmailAuth from "../components/EmailAuth";
import UserAvatar from "../components/UserAvatar";
import { coverImageFor } from "../lib/stockImages";
import { MudBadge } from "../components/MudBadge";
import WhatsNewModal from "../components/WhatsNewModal";
import bundledChangelog from "../lib/changelog.json";
import { ACHIEVEMENTS, ALL_ACHIEVEMENT_KEYS } from "../lib/achievements";

const LAST_SEEN_KEY = "td_seen_version";
const SHOW_WELCOME_KEY = "td_always_show_welcome";

const PACE_OPTIONS = ["Casual", "Active", "Endurance"];
const TRANSIT_OPTIONS = ["Transit", "Car"];
const ACTIVITY_OPTIONS = ["Senior", "Moderate", "High"];

function UnitsToggle() {
  const { units, setUnits } = useUnits();
  return (
    <section className="td-card p-5 sm:p-6 mt-5">
      <div className="td-section-label">Distance units</div>
      <div className="mt-3 inline-flex bg-brand-sand border border-brand-border rounded-full p-1 text-sm" data-testid="units-toggle">
        <button
          data-testid="units-km"
          onClick={() => setUnits("km")}
          className={`px-5 py-1.5 rounded-full ${units === "km" ? "bg-brand-primary text-brand-sand" : "text-brand-subtle"}`}
        >
          km
        </button>
        <button
          data-testid="units-mi"
          onClick={() => setUnits("mi")}
          className={`px-5 py-1.5 rounded-full ${units === "mi" ? "bg-brand-primary text-brand-sand" : "text-brand-subtle"}`}
        >
          mi
        </button>
      </div>
    </section>
  );
}

/**
 * Compact footer toggle — replaces the old full-card Welcome Screen section.
 * Sits below My Walks as a plain-text row so the primary Profile canvas stays
 * focused on the two Matchmaker cards (My Pack + My Pace).
 */
function WelcomeFooterToggle() {
  const navigate = useNavigate();
  const [always, setAlways] = useState(() => localStorage.getItem(SHOW_WELCOME_KEY) === "true");
  useEffect(() => {
    localStorage.setItem(SHOW_WELCOME_KEY, always ? "true" : "false");
  }, [always]);
  const replay = () => {
    localStorage.removeItem("td_onboarded");
    navigate("/");
  };
  return (
    <div
      className="mt-4 px-1 flex items-center justify-between text-xs text-brand-subtle"
      data-testid="welcome-footer-toggle"
    >
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          data-testid="always-welcome-toggle"
          checked={always}
          onChange={(e) => setAlways(e.target.checked)}
          className="accent-brand-primary"
        />
        <span>Always show welcome on launch</span>
      </label>
      <button
        data-testid="replay-welcome-btn"
        onClick={replay}
        className="inline-flex items-center gap-1 hover:text-brand-text hover:underline"
      >
        <RotateCcw size={11} /> Replay
      </button>
    </div>
  );
}

/**
 * My Pack — the four-legged half of the Matchmaker profile. Saves via
 * PATCH /api/auth/me/matchmaker-profile so a walker can iterate one card
 * without touching the other.
 */
function MyPackCard() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState(() => ({
    name: user?.dogProfile?.name || "",
    breed: user?.dogProfile?.breed || "",
    age: user?.dogProfile?.age != null ? String(user.dogProfile.age) : "",
    activityLevel: user?.dogProfile?.activityLevel || "",
  }));
  const [saving, setSaving] = useState(false);

  // Re-hydrate whenever the auth user shape changes (e.g. after login).
  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.dogProfile?.name || "",
      breed: user.dogProfile?.breed || "",
      age: user.dogProfile?.age != null ? String(user.dogProfile.age) : "",
      activityLevel: user.dogProfile?.activityLevel || "",
    });
  }, [user]);

  if (!user) return null;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/me/matchmaker-profile", {
        dogProfile: {
          name: form.name.trim(),
          breed: form.breed.trim(),
          age: form.age === "" ? null : parseFloat(form.age),
          activityLevel: form.activityLevel || null,
        },
      });
      toast.success("Pack saved");
      // Refresh the auth context so downstream Matchmaker calls read the new shape.
      if (refresh) await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save pack profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="td-card p-5 sm:p-6 mt-5" data-testid="my-pack-card">
      <div className="flex items-center gap-2">
        <PawPrint size={16} className="text-brand-accent" />
        <h2 className="font-heading text-lg font-medium">My Pack</h2>
      </div>
      <p className="text-sm text-brand-subtle mt-1">
        Tell the Matchmaker about your dog — we&apos;ll weight suggestions by breed, age and energy.
      </p>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="td-section-label">Dog&apos;s name</label>
          <input
            data-testid="pack-name"
            className="td-input mt-1"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. Ozzy"
            maxLength={60}
          />
        </div>
        <div>
          <label className="td-section-label">Breed</label>
          <input
            data-testid="pack-breed"
            className="td-input mt-1"
            value={form.breed}
            onChange={(e) => set("breed", e.target.value)}
            placeholder="e.g. Border Collie"
            maxLength={80}
          />
        </div>
        <div>
          <label className="td-section-label">Age (years)</label>
          <input
            data-testid="pack-age"
            type="number"
            step="0.5"
            min="0"
            max="30"
            className="td-input mt-1"
            value={form.age}
            onChange={(e) => set("age", e.target.value)}
            placeholder="e.g. 4"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="td-section-label">Activity level</label>
          <select
            data-testid="pack-activity"
            className="td-input mt-1"
            value={form.activityLevel}
            onChange={(e) => set("activityLevel", e.target.value)}
          >
            <option value="">Choose…</option>
            {ACTIVITY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      </div>

      <button
        data-testid="pack-save"
        onClick={save}
        disabled={saving}
        className="td-btn-primary mt-5 !py-2.5"
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        Save pack
      </button>
    </section>
  );
}

/**
 * My Pace — the human half of the profile. Two simple selects so the
 * Matchmaker can rule out routes that don't match the walker's shift +
 * transit reality.
 */
function MyPaceCard() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState(() => ({
    pace: user?.humanProfile?.pace || "",
    transitReliance: user?.humanProfile?.transitReliance || "",
  }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      pace: user.humanProfile?.pace || "",
      transitReliance: user.humanProfile?.transitReliance || "",
    });
  }, [user]);

  if (!user) return null;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/me/matchmaker-profile", {
        humanProfile: {
          pace: form.pace || null,
          transitReliance: form.transitReliance || null,
        },
      });
      toast.success("Pace saved");
      if (refresh) await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save pace profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="td-card p-5 sm:p-6 mt-5" data-testid="my-pace-card">
      <div className="flex items-center gap-2">
        <Footprints size={16} className="text-brand-primary" />
        <h2 className="font-heading text-lg font-medium">My Pace</h2>
      </div>
      <p className="text-sm text-brand-subtle mt-1">
        How you walk, and how you get to the trailhead.
      </p>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="td-section-label">Pace</label>
          <select
            data-testid="pace-select"
            className="td-input mt-1"
            value={form.pace}
            onChange={(e) => set("pace", e.target.value)}
          >
            <option value="">Choose…</option>
            {PACE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className="td-section-label">Transit reliance</label>
          <select
            data-testid="transit-reliance-select"
            className="td-input mt-1"
            value={form.transitReliance}
            onChange={(e) => set("transitReliance", e.target.value)}
          >
            <option value="">Choose…</option>
            {TRANSIT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      </div>

      <button
        data-testid="pace-save"
        onClick={save}
        disabled={saving}
        className="td-btn-primary mt-5 !py-2.5"
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        Save pace
      </button>
    </section>
  );
}

function MyWalksSection() {
  const { user } = useAuth();
  const favs = useQuery({
    queryKey: ["my-walks"],
    enabled: !!user,
    queryFn: async () => (await api.get("/favorites")).data,
  });

  if (!user) return null;
  const items = favs.data?.items || [];

  return (
    <section className="td-card p-5 sm:p-6 mt-5" data-testid="my-walks-section">
      <div className="flex items-center gap-2">
        <BookHeart size={16} className="text-brand-accent" />
        <h2 className="font-heading text-lg font-medium">My Walks</h2>
        <span className="text-xs text-brand-subtle ml-auto">{items.length} saved</span>
      </div>
      {favs.isLoading ? (
        <div className="text-sm text-brand-subtle mt-3">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-brand-subtle mt-3">
          Tap the heart on any trail to save it to your diary.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          {items.slice(0, 8).map((l) => (
            <Link
              key={l.location_id}
              to={`/location/${l.location_id}`}
              data-testid={`my-walk-${l.location_id}`}
              className="td-card overflow-hidden flex items-center gap-3 p-3 hover:-translate-y-0.5 transition-all"
            >
              <div className="w-16 h-16 rounded-2xl overflow-hidden bg-brand-border/30 shrink-0">
                <img src={coverImageFor(l)} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs text-brand-subtle flex items-center gap-1">
                  {l.type === "camping" ? <Tent size={12} /> : <Mountain size={12} />}
                  <span className="truncate">{l.region || "United Kingdom"}</span>
                </div>
                <div className="font-heading text-base truncate">{l.name}</div>
                <div className="mt-1">
                  <MudBadge avg={l.mud_level_avg} count={l.mud_reports_count} testid={`my-walk-mud-${l.location_id}`} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
      {items.length > 8 && (
        <Link to="/favourites" className="td-btn-secondary !py-2 !text-sm mt-4 inline-flex">
          See all {items.length} →
        </Link>
      )}

      {/* Compact welcome-screen toggle — sits inside the My Walks card footer
          so the primary Profile canvas stays focused on the Matchmaker cards. */}
      <WelcomeFooterToggle />
    </section>
  );
}

// ─── Honours (Trophy Wall) — earned + locked slots with tap-for-lore inline box. ───
function HonoursSection({ achievements, verifiedScoutsCount = 0 }) {
  const [open, setOpen] = useState(null); // achievement key with visible lore
  const earned = new Set(achievements || []);
  const activeMeta = open ? ACHIEVEMENTS[open] : null;
  // Scout Streak Whisper — a discreet muted line under the rune grid that
  // counts down to the next tier so walkers know how close they are to
  // the next unlock without opening every rune. Uses ONLY the existing
  // `verified_scouts_count` derived from the users doc — no new schema.
  const whisper = React.useMemo(() => {
    const c = Math.max(0, Math.floor(verifiedScoutsCount || 0));
    const hasTrailScout = earned.has("trail_scout");
    if (c === 0 && !hasTrailScout) {
      // Hide once the walker already holds Trail Scout — the first-rune
      // nudge is irrelevant to someone who's past that milestone (they
      // might still have c === 0 if their count hasn't propagated yet,
      // or they earned it on a legacy flow).
      return "Verify a Scout Needed trail to earn your first Scout rune, The Trail Scout.";
    }
    if (c > 0 && c < 5 && !hasTrailScout) {
      const n = 5 - c;
      return `You are ${n} trail${n === 1 ? "" : "s"} away from The Trail Scout.`;
    }
    if (c < 25 && hasTrailScout) {
      const n = 25 - c;
      if (n <= 0) return "Every scout rune is yours — a true wanderer of the pack.";
      return `You are ${n} trail${n === 1 ? "" : "s"} away from The Master Scout.`;
    }
    if (c < 25) {
      // Fallback for the odd case where neither branch above fired
      // (shouldn't normally hit, but keeps the whisper line stable).
      const n = 25 - c;
      return `You are ${n} trail${n === 1 ? "" : "s"} away from The Master Scout.`;
    }
    return "Every scout rune is yours — a true wanderer of the pack.";
  }, [verifiedScoutsCount, earned]);

  return (
    <div className="mt-4 pt-4 border-t border-brand-border/50" data-testid="profile-honours">
      <div className="td-section-label !mt-0">Honours</div>
      
      {/* Icon Grid */}
      <div className="mt-3 flex flex-wrap gap-3">
        {ALL_ACHIEVEMENT_KEYS.map((k) => {
          const meta = ACHIEVEMENTS[k];
          const { Icon, name } = meta;
          const isEarned = earned.has(k);
          const isOpen = open === k;
          
          return (
            <button
              key={k}
              type="button"
              data-testid={`honour-${k}`}
              data-earned={isEarned ? "true" : "false"}
              aria-label={name}
              onClick={() => setOpen(isOpen ? null : k)}
              className={`w-14 h-14 rounded-2xl border flex items-center justify-center transition ${
                isEarned
                  ? "bg-brand-primary/10 border-brand-primary text-brand-primary"
                  : "bg-brand-sand border-brand-border text-brand-subtle opacity-30"
              } ${isOpen ? "ring-2 ring-brand-primary ring-offset-2 ring-offset-brand-sand" : ""}`}
            >
              <Icon size={26} />
            </button>
          );
        })}
      </div>
      
      {/* Inline Lore Box (Replaces the broken mobile tooltip) */}
      {activeMeta ? (
        <div 
          data-testid="honour-lore-inline"
          className="mt-4 p-4 rounded-xl bg-brand-text text-brand-sand text-sm leading-relaxed shadow-sm transition-all"
        >
          <div className="font-heading font-medium text-brand-accent mb-1 flex justify-between items-start">
            <span>{activeMeta.name}</span>
            <button 
              onClick={() => setOpen(null)} 
              className="text-xl leading-none opacity-70 hover:opacity-100 p-1 -mr-2 -mt-2"
              aria-label="Close"
            >
              &times;
            </button>
          </div>
          <div className="opacity-90">{activeMeta.lore}</div>
        </div>
      ) : (
        <p className="text-[11px] text-brand-subtle mt-2 transition-all">
          Tap a rune to see the lore behind each honour.
        </p>
      )}
      {/* Scout Streak Whisper — muted, italicised nudge that mirrors the
          existing lore-hint styling so it feels part of the Trophy Wall
          rather than a stat panel. Non-earners still see the invite line. */}
      <p
        data-testid="scout-streak-whisper"
        className="mt-2 text-[11px] italic text-brand-subtle/80 tracking-wide"
      >
        {whisper}
      </p>
    </div>
  );
}

/**
 * AvatarUploader — Profile-page control for the walker's photo.
 *
 * UX contract:
 *   • Tap the avatar → opens the hidden file picker.
 *   • After picking, show a preview and a two-button bar: Save / Cancel.
 *   • "Remove photo" is a small inline link that only renders when the
 *     walker already has an uploaded `avatar_url`. It falls back to
 *     their Google picture, or the brand-green initials disc.
 *   • Inline error states for wrong file type / >5MB — never a crash.
 */
const AVATAR_ACCEPT = "image/jpeg,image/png,image/webp";
const AVATAR_MAX_BYTES = 5 * 1024 * 1024;
function AvatarUploader({ user, onChanged }) {
  const fileRef = useRef(null);
  const [picked, setPicked] = useState(null); // { file, previewUrl }
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => () => {
    if (picked?.previewUrl) URL.revokeObjectURL(picked.previewUrl);
  }, [picked]);

  const pickFile = () => {
    setError("");
    fileRef.current?.click();
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!f) return;
    if (!AVATAR_ACCEPT.split(",").includes(f.type)) {
      setError("Please pick a JPEG, PNG or WebP image.");
      return;
    }
    if (f.size > AVATAR_MAX_BYTES) {
      setError("That photo is over 5MB — try a smaller one.");
      return;
    }
    setPicked({ file: f, previewUrl: URL.createObjectURL(f) });
  };

  const cancel = () => {
    if (picked?.previewUrl) URL.revokeObjectURL(picked.previewUrl);
    setPicked(null);
    setError("");
  };

  const save = async () => {
    if (!picked) return;
    setSaving(true);
    setError("");
    const fd = new FormData();
    fd.append("file", picked.file);
    try {
      await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Profile photo updated");
      cancel();
      onChanged && onChanged();
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't upload — try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await api.delete("/profile/avatar");
      toast.success("Profile photo removed");
      onChanged && onChanged();
    } catch {
      toast.error("Couldn't remove photo");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="flex flex-col items-start gap-2" data-testid="avatar-uploader">
      <button
        type="button"
        onClick={pickFile}
        className="relative w-16 h-16 rounded-full overflow-hidden ring-0 focus:ring-2 focus:ring-brand-primary focus:outline-none group"
        aria-label="Change profile photo"
        data-testid="avatar-change-btn"
      >
        {picked ? (
          <img src={picked.previewUrl} alt="Preview" className="w-full h-full object-cover" />
        ) : (
          <UserAvatar user={user} size={64} />
        )}
        <span className="absolute inset-0 bg-brand-text/0 group-hover:bg-brand-text/35 transition-colors flex items-center justify-center text-brand-sand opacity-0 group-hover:opacity-100">
          <Camera size={18} />
        </span>
      </button>
      <input
        ref={fileRef}
        type="file"
        accept={AVATAR_ACCEPT}
        onChange={onFileChange}
        className="hidden"
        data-testid="avatar-file-input"
      />
      {error && (
        <div className="text-xs text-brand-error max-w-[12rem]" data-testid="avatar-error">
          {error}
        </div>
      )}
      {picked && (
        <div className="flex gap-2 mt-1" data-testid="avatar-preview-actions">
          <button
            onClick={save}
            disabled={saving}
            data-testid="avatar-save-btn"
            className="td-btn-primary !py-1 !px-3 !text-xs disabled:opacity-60"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
          </button>
          <button
            onClick={cancel}
            disabled={saving}
            data-testid="avatar-cancel-btn"
            className="td-btn-secondary !py-1 !px-3 !text-xs"
          >
            <X size={12} /> Cancel
          </button>
        </div>
      )}
      {!picked && user?.avatar_url && (
        <button
          onClick={remove}
          disabled={removing}
          data-testid="avatar-remove-btn"
          className="text-xs text-brand-subtle hover:text-brand-error inline-flex items-center gap-1"
        >
          {removing ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />} Remove photo
        </button>
      )}
    </div>
  );
}

// ─── Walked trails — private per-user log (Phase 1 + Phase 2 grader). ───
const WALK_STATUS_META = {
  completed:    { label: "Completed",  color: "#4A8B5A", bg: "rgba(74,139,90,0.15)" },
  partly_walked:{ label: "Partly",     color: "#D97A2C", bg: "rgba(217,122,44,0.15)" },
  unverified:   { label: "Unverified", color: "#8B7A5A", bg: "rgba(139,122,90,0.15)" },
};
function WalkStatusChip({ status, testId }) {
  const meta = WALK_STATUS_META[status] || WALK_STATUS_META.unverified;
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center rounded-full font-semibold text-[11px] px-2 py-0.5"
      style={{ color: meta.color, background: meta.bg, borderWidth: 1, borderColor: meta.color }}
    >
      {meta.label}
    </span>
  );
}
function WalkedTrailsSection() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [confirmDel, setConfirmDel] = useState(null); // trail_id with pending delete
  const q = useQuery({
    queryKey: ["walks-me"],
    queryFn: async () => (await api.get("/walks/me")).data,
    enabled: !!user,
  });
  if (!user) return null;
  const trails = q.data?.trails || [];
  const total = q.data?.total || 0;
  const completedTrails = q.data?.completed_trails || 0;
  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    } catch { return ""; }
  };
  const fmtDuration = (s) => {
    if (!s) return "—";
    if (s < 60) return "under 1 min";
    const m = Math.round(s / 60);
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  };
  const del = async (trailId) => {
    try {
      await api.delete(`/walks/trail/${trailId}`);
      toast.success("Walks removed");
      qc.invalidateQueries({ queryKey: ["walks-me"] });
    } catch {
      toast.error("Couldn't remove walks");
    } finally {
      setConfirmDel(null);
    }
  };
  return (
    <section className="td-card p-5 sm:p-6 mt-5" data-testid="walked-trails-section">
      <div className="flex items-center justify-between">
        <div>
          <div className="td-section-label !mt-0">Walked trails</div>
          <div className="font-heading text-base mt-0.5" data-testid="walked-trails-total">
            {total === 0 ? "No walks logged yet" : `${total} walk${total === 1 ? "" : "s"}`}
            {completedTrails > 0 && (
              <span className="text-sm text-brand-subtle font-normal" data-testid="walked-trails-completed-count">
                {" · "}{completedTrails} completed
              </span>
            )}
          </div>
        </div>
        <Footprints size={18} className="text-brand-primary" />
      </div>
      {trails.length === 0 ? (
        <div className="mt-3 text-sm text-brand-subtle" data-testid="walked-trails-empty">
          No walks logged yet — pick a trail and tap Start walk.
        </div>
      ) : (
        <ul className="mt-3 space-y-2">
          {trails.map((t) => (
            <li
              key={t.trail_id}
              className="flex items-center gap-2 border border-brand-border/60 rounded-2xl p-3"
              data-testid={`walked-trail-row-${t.trail_id}`}
            >
              <Link to={`/location/${t.trail_id}`} className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-brand-text truncate">{t.trail_name || t.trail_id}</span>
                  <WalkStatusChip status={t.latest_status || "unverified"} testId={`walked-trail-status-${t.trail_id}`} />
                </div>
                <div className="text-xs text-brand-subtle mt-0.5">
                  Last walked {fmtDate(t.last_walked_at)} · {fmtDuration(t.last_duration_s)}
                  {t.count > 1 && (
                    <> · walked {t.count} times{t.completed_count > 0 && ` · completed ${t.completed_count}`}</>
                  )}
                </div>
              </Link>
              <button
                data-testid={`walked-trail-delete-${t.trail_id}`}
                onClick={() => setConfirmDel(t.trail_id)}
                className="w-9 h-9 rounded-full text-brand-subtle hover:text-brand-error flex items-center justify-center"
                aria-label="Remove walks for this trail"
              >
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {confirmDel && (
        <div className="fixed inset-0 z-[1100] bg-brand-text/60 flex items-center justify-center p-4" onClick={() => setConfirmDel(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-brand-surface rounded-3xl w-full max-w-sm p-6">
            <h3 className="font-heading text-xl">Remove these walks?</h3>
            <p className="text-sm text-brand-subtle mt-2">Every walk you've logged for this trail will be forgotten. The trail itself stays.</p>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setConfirmDel(null)} className="td-btn-secondary flex-1 !py-2.5">Cancel</button>
              <button onClick={() => del(confirmDel)} className="td-btn-primary flex-1 !py-2.5 !bg-brand-error">Remove</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}


export default function Profile() {
  const { user, signOut, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  if (loading) return <div className="p-8 text-brand-subtle">Loading…</div>;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
      <header className="mb-6">
        <div className="td-section-label">Profile</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-brand-text mt-1">Your account</h1>
      </header>

      {user ? (
        <div className="td-card p-5 sm:p-6" data-testid="profile-card">
          <div className="flex items-center gap-4">
            <AvatarUploader user={user} onChanged={() => refresh && refresh()} />
            <div className="flex-1 min-w-0">
              <div className="font-heading text-xl truncate">{user.name || user.email.split("@")[0]}</div>
              <div className="text-sm text-brand-subtle truncate">{user.email}</div>
            </div>
            <button data-testid="signout-btn" onClick={signOut} className="td-btn-secondary !py-2 !px-4 !text-sm">
              <LogOut size={15} /> Sign out
            </button>
          </div>

          {/* Earned badges — gold/brown palette matches the Scout Needed
              UI so walkers instantly connect "I did that" with "I earned
              this". Only renders when the walker has at least one badge
              so unearned users don't see an empty row. */}
          {(user.badges || []).length > 0 && (
            <div
              className="mt-4 pt-4 border-t border-brand-border/50 flex flex-wrap gap-2"
              data-testid="profile-badges"
            >
              {(user.badges || []).includes("first_scout") && (
                <span
                  data-testid="badge-first-scout"
                  title="Awarded for casting the resolving vote on a Scout Needed trail"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide bg-[#F5B841] text-[#2C3A2E] border border-[#B8862A] shadow-sm shadow-[#B8862A]/30"
                >
                  <Target size={13} strokeWidth={2.75} className="text-[#2C3A2E]" />
                  First Scout
                </span>
              )}
            </div>
          )}

          {/* Dog's name — sourced from the existing "My Pack" (Matchmaker)
              dogProfile.name — walkers set it once there. No separate
              input on this page. */}

          {/* Trophy Wall — Norse achievements grid. Earned = brand-primary,
              locked = 30% opacity. Tap = lore popover. */}
          <HonoursSection
            achievements={user.achievements || []}
            verifiedScoutsCount={user.verified_scouts_count || 0}
          />

          {/* Email notification toggle for review likes + Ozzy approvals.
              Default on, stored on the user doc. The unsubscribe link in
              every email writes the same field. */}
          <NotificationsToggle />

          {/* Danger zone — right to erasure. Collapsed at the bottom so a
              scrolled-up walker never fires it by accident. Confirm dialog
              requires typing DELETE verbatim. */}
          <DeleteAccountSection />
        </div>
      ) : (
        <div className="td-card p-5 sm:p-6" data-testid="profile-card-anon">
          <div className="flex items-center gap-4">
            <UserAvatar user={{ name: "Walker" }} size={64} />
            <div className="flex-1 min-w-0">
              <div className="font-heading text-xl">Walker</div>
              <div className="text-sm text-brand-subtle">Sign in to sync favourites and offline routes.</div>
            </div>
          </div>
          <div className="mt-5">
            <EmailAuth onSignedIn={() => navigate("/discover")} />
          </div>
        </div>
      )}

      {/* Matchmaker cards — only shown to signed-in walkers because the
          fields have to persist server-side. Anonymous walkers see the
          sign-in prompt above instead. */}
      <MyPackCard />
      <MyPaceCard />

      <UnitsToggle />
      <MyWalksSection />
      <WalkedTrailsSection />

      <section className="td-card p-5 sm:p-6 mt-5" data-testid="whats-new-section">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-brand-accent/10 flex items-center justify-center shrink-0">
            <Sparkles size={18} className="text-brand-accent" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="td-section-label">Updates</div>
            <div className="font-heading text-base text-brand-text mt-0.5">What&apos;s new in TrailDog</div>
          </div>
          <button
            data-testid="whats-new-open"
            onClick={() => setWhatsNewOpen(true)}
            className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap"
          >
            View
          </button>
        </div>
      </section>

      <section className="mt-8 text-center text-xs text-brand-subtle">
        <Compass size={14} className="inline -mt-0.5" /> TrailDog · Ozzy&apos;s Basecamp
      </section>

      <WhatsNewModal
        open={whatsNewOpen}
        onClose={() => {
          setWhatsNewOpen(false);
          // Stamp the "seen" cursor from whichever changelog view produced
          // the modal — API-driven data if it resolved, bundled JSON as
          // a fallback. Never leave the cursor blank or the modal will
          // spring open again on the next mount.
          try {
            const apiEntries = qc.getQueryData(["changelog"]);
            const v = apiEntries?.[0]?.version || bundledChangelog[0]?.version || "";
            if (v) localStorage.setItem(LAST_SEEN_KEY, v);
          } catch { /* ignore */ }
        }}
      />
    </div>
  );
}


// Simple preference row for "Email me when my reviews get likes or
// approvals". Fetches the current value on mount, patches on toggle.
// Failures stay quiet — the backend side of the toggle is best-effort
// and controlled by the same flag the one-click unsubscribe link sets.
function NotificationsToggle() {
  const prefs = useQuery({
    queryKey: ["profile-notifications"],
    queryFn: async () => (await api.get("/profile/notifications")).data,
    staleTime: 60_000,
  });
  const qc = useQueryClient();
  const on = prefs.data?.notify_reviews !== false;

  const toggle = async (next) => {
    try {
      await api.patch("/profile/notifications", { notify_reviews: next });
      qc.invalidateQueries({ queryKey: ["profile-notifications"] });
      toast.success(next ? "Email notifications on" : "Email notifications off");
    } catch {
      toast.error("Couldn't save");
    }
  };

  return (
    <div
      className="mt-4 pt-4 border-t border-brand-border/50"
      data-testid="profile-notifications"
    >
      <div className="td-section-label">Notifications</div>
      <label className="flex items-start gap-3 mt-2 cursor-pointer">
        <input
          type="checkbox"
          data-testid="profile-notify-reviews"
          checked={on}
          onChange={(e) => toggle(e.target.checked)}
          className="mt-1 accent-brand-primary"
        />
        <div className="text-sm">
          <div className="text-brand-text">Email me when my reviews get likes or approvals</div>
          <div className="text-xs text-brand-subtle">
            Daily summary for helpful votes, one-off for Ozzy approvals. Unsubscribe link
            in every email.
          </div>
        </div>
      </label>
    </div>
  );
}



// Danger zone: delete-my-account. Confirm dialog requires typing DELETE
// and surfaces the "also delete reviews & photos" opt-in. All derived
// values declared before the hooks/handlers below, per the project rule.
function DeleteAccountSection() {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [alsoDelete, setAlsoDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const canSubmit = confirmText === "DELETE" && !busy;

  const close = () => {
    if (busy) return;
    setOpen(false);
    setConfirmText("");
    setAlsoDelete(false);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.delete("/me", { data: { confirm: "DELETE", delete_content: alsoDelete } });
      toast.success("Your account has been deleted. Thanks for walking with us.");
      // signOut tears down the local session; always navigate to home
      // afterwards so a stale route can't try to re-fetch /me.
      try { await signOut(); } catch { /* no-op */ }
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete the account — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mt-6 pt-4 border-t border-brand-error/30"
      data-testid="profile-danger-zone"
    >
      <div className="td-section-label !text-brand-error">Danger zone</div>
      <p className="text-xs text-brand-subtle mt-1 mb-2">
        Permanent and irreversible. Removes your profile, sessions, saved trails and walks.
      </p>
      <button
        type="button"
        data-testid="delete-account-btn"
        onClick={() => setOpen(true)}
        className="td-btn-secondary !text-brand-error !border-brand-error/40 !py-2 !px-4 !text-sm"
      >
        <Trash2 size={14} /> Delete my account
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 p-4"
          data-testid="delete-account-modal"
          onClick={close}
        >
          <div
            className="td-card max-w-md w-full p-5 sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="td-section-label !text-brand-error">Delete my account</div>
            <p className="text-sm mt-2">
              This will remove your profile, sessions, saved trails and walks. By default
              your reviews and photos stay (shown as <strong>Former walker</strong>) so the
              trail record stays useful.
            </p>
            <label className="flex items-start gap-2 mt-3 text-sm cursor-pointer">
              <input
                type="checkbox"
                data-testid="delete-also-content"
                checked={alsoDelete}
                onChange={(e) => setAlsoDelete(e.target.checked)}
                className="mt-1 accent-brand-primary"
              />
              <span>Also delete my reviews and photos</span>
            </label>
            <label className="block mt-4 text-xs text-brand-subtle">
              Type <span className="font-mono font-semibold text-brand-text">DELETE</span> to confirm:
            </label>
            <input
              data-testid="delete-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="td-input mt-1"
              autoComplete="off"
              spellCheck={false}
            />
            <div className="flex gap-2 justify-end mt-4">
              <button
                type="button"
                data-testid="delete-cancel"
                onClick={close}
                disabled={busy}
                className="td-btn-secondary !py-2 !px-4 !text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="delete-confirm"
                onClick={submit}
                disabled={!canSubmit}
                className="td-btn-primary !py-2 !px-4 !text-sm !bg-brand-error disabled:opacity-50"
              >
                {busy ? "Deleting…" : "Delete my account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

